import { useState, useCallback, useEffect } from 'react';
import { dispatcher } from 'dispatcher';
import { useIsMounted } from 'js/hooks/useIsMounted';
import { usePrevious } from 'js/hooks/usePrevious';
import { prop } from 'ramda';
import { useDeviceSelector } from 'js/store/hooks';
import { getCleanUrl } from 'js/utils/url.util';

export const useHideJackpotsUnderPopup = ({
    isInGamePortlet = true,
    isAnimated = false
}) => {
    const [isVisible, setIsVisible] = useState(true);
    const [isPopupOpened, setIsPopupOpened] = useState(false);
    const isDesktop = useDeviceSelector(prop('isDesktop'));

    // State to handle opening info popup by direct link
    const pathname = getCleanUrl(window.location.pathname);
    const isPageInPopupUrl = new RegExp(['/igaming/games-info/', '/online-game/'].join('|')).test(pathname);
    const [isPageInPopup, setIsPageInPopup] = useState(isPageInPopupUrl);

    // Handle animation
    const prevIsAnimated = usePrevious(isAnimated);
    const currentIsAnimated = isAnimated === prevIsAnimated ? prevIsAnimated : isAnimated;
    const shouldShowAnimation = !isInGamePortlet ? currentIsAnimated : currentIsAnimated && isVisible;

    // Ticker should be always shown in popup and for desktop
    const shouldShowTicker = !isInGamePortlet || isDesktop ? true : isVisible;

    const isMounted = useIsMounted();

    useEffect(() => {
        const handleOpen = () => {
            setIsPopupOpened(true);
        };

        const handleClose = () => {
            setIsPopupOpened(false);
            setIsPageInPopup(false);
        };

        dispatcher.listen('closeGamesInfoPopupAction', handleClose);
        dispatcher.listen('openGamesInfoPopupAction', handleOpen);
        return () => {
            dispatcher.removeListener('closeGamesInfoPopupAction', handleClose);
            dispatcher.removeListener('openGamesInfoPopupAction', handleOpen);
        };
    }, []);

    // Function should be passed as ref
    const getIsVisibleRef = useCallback(
        () => {
            if (isMounted()) {
                // Handle closing popup which have been opened by direct link for Jeckpots from the Games portlet
                if (isPageInPopup && !isPopupOpened) {
                    setIsVisible(!isPageInPopup);
                    return;
                }

                setIsVisible(!isPopupOpened);
            }
        },
        [isMounted, isPageInPopup, isPopupOpened]
    );

    return {
        shouldShowAnimation,
        shouldShowTicker,
        getIsVisibleRef,
        currentIsAnimated,
        isVisible
    };
};
